import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import {
  Home,
  Clock,
  Star,
  Workflow,
  Search,
  ChevronsLeft,
  ChevronsRight,
  Sparkles,
  X,
} from 'lucide-react';
import { useApp } from '@/store/app';
import { CATEGORIES } from '@/registry/categories';
import { TOOLS_BY_CATEGORY } from '@/registry';
import { cn } from '@/lib/cn';

export function Sidebar() {
  const {
    sidebarCollapsed,
    toggleSidebar,
    togglePalette,
    toggleAgent,
    mobileSidebarOpen,
    setMobileSidebarOpen,
  } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  /* Close drawer when route changes (mobile) */
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname, setMobileSidebarOpen]);

  /* Lock body scroll while drawer open */
  useEffect(() => {
    if (mobileSidebarOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [mobileSidebarOpen]);

  return (
    <>
      {/* Mobile backdrop */}
      <div
        onClick={() => setMobileSidebarOpen(false)}
        className={cn(
          'fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden transition-opacity duration-200',
          mobileSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      />

      <aside
        className={cn(
          'z-50 h-screen bg-bg-subtle/95 lg:bg-bg-subtle/50 flex flex-col overflow-hidden transition-all duration-300',
          /* Desktop: inline column with width-based collapse */
          'lg:relative lg:shrink-0 lg:translate-x-0 lg:border-r lg:border-border',
          sidebarCollapsed ? 'lg:w-16' : 'lg:w-[264px]',
          /* Mobile: fixed drawer that slides from the left */
          'fixed inset-y-0 left-0 w-[280px] border-r-2 border-fg/85 dark:border-fg/30',
          mobileSidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0',
        )}
      >
      {/* Brand */}
      <div className="px-4 h-14 flex items-center justify-between border-b border-border">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2.5 group"
        >
          <div className="size-9 rounded-xl bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 border-2 border-fg/85 dark:border-fg/40 shadow-sticker-sm flex items-center justify-center text-white font-black text-base transition-transform group-hover:scale-110 group-hover:rotate-[-8deg] [text-shadow:0_1px_0_rgba(0,0,0,0.25)]">
            U
          </div>
          <div className={cn('flex flex-col items-start leading-none', sidebarCollapsed && 'lg:hidden')}>
            <span className="text-sm font-semibold tracking-tight">Utility OS</span>
            <span className="text-2xs text-fg-subtle mt-0.5">for work</span>
          </div>
        </button>
        {/* Mobile close (drawer) */}
        <button
          onClick={() => setMobileSidebarOpen(false)}
          className="p-1.5 rounded text-fg-subtle hover:text-fg hover:bg-surface-hover transition lg:hidden"
          aria-label="Close menu"
        >
          <X size={16} />
        </button>
        {/* Desktop collapse */}
        <button
          onClick={toggleSidebar}
          className={cn(
            'hidden lg:flex p-1 rounded text-fg-subtle hover:text-fg hover:bg-surface-hover transition',
            sidebarCollapsed && 'absolute right-2 top-3.5',
          )}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
        </button>
      </div>

      {/* Search trigger */}
      <div className="p-3">
        <button
          onClick={togglePalette}
          className={cn(
            'w-full flex items-center gap-2 h-9 px-3 rounded-lg',
            'bg-bg-elev border-2 border-fg/85 dark:border-fg/40 shadow-sticker-sm',
            'hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-sticker',
            'active:translate-x-[1px] active:translate-y-[1px] active:shadow-sticker-pressed',
            'transition-all text-fg-subtle text-sm group',
          )}
        >
          <Search size={14} className="shrink-0 group-hover:text-fg-muted transition-colors" />
          <span className={cn('flex-1 text-left truncate font-medium', sidebarCollapsed && 'lg:hidden')}>
            Search or run…
          </span>
          <kbd className={cn(sidebarCollapsed && 'lg:hidden')}>⌘K</kbd>
        </button>

        <button
          onClick={toggleAgent}
          className={cn(
            'mt-2 w-full flex items-center gap-2 h-9 px-3 rounded-lg bg-amber-300/70 dark:bg-amber-400/15 border-2 border-fg/85 dark:border-fg/40 shadow-sticker-sm hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-sticker active:translate-x-[1px] active:translate-y-[1px] active:shadow-sticker-pressed transition-all text-sm font-semibold group',
            sidebarCollapsed && 'lg:hidden',
          )}
        >
          <Sparkles size={14} className="text-orange-600 dark:text-amber-400 group-hover:scale-110 group-hover:rotate-12 transition-transform" />
          <span className="flex-1 text-left text-fg">Ask the agent</span>
        </button>
      </div>

      {/* Top nav */}
      <nav className="flex-1 px-3 overflow-y-auto pb-4 space-y-5">
        <div className="space-y-0.5">
          <NavBtn to="/" icon={Home} label="Home" collapsed={sidebarCollapsed} end />
          <NavBtn to="/recent" icon={Clock} label="Recent" collapsed={sidebarCollapsed} />
          <NavBtn to="/favorites" icon={Star} label="Favorites" collapsed={sidebarCollapsed} />
          <NavBtn to="/workflows" icon={Workflow} label="Workflows" collapsed={sidebarCollapsed} />
        </div>

        {/* Full categories list — hide on lg+ only when collapsed */}
        <div className={cn(sidebarCollapsed && 'lg:hidden')}>
          <div className="px-3 mb-1.5 text-2xs font-medium uppercase tracking-wider text-fg-subtle">
            Categories
          </div>
          <div className="space-y-0.5">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const count = TOOLS_BY_CATEGORY[c.id]?.length ?? 0;
              return (
                <NavLink
                  key={c.id}
                  to={`/c/${c.id}`}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 h-8 px-3 rounded-md text-sm transition-all group',
                      isActive
                        ? 'bg-surface text-fg shadow-sm'
                        : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
                    )
                  }
                >
                  <span
                    className={cn(
                      'size-5 rounded-md flex items-center justify-center bg-gradient-to-br shrink-0 border border-fg/85 dark:border-fg/40 group-hover:rotate-[-6deg] transition-transform',
                      c.gradient,
                    )}
                  >
                    <Icon size={11} className="text-white drop-shadow-[0_1px_0_rgba(0,0,0,0.25)]" />
                  </span>
                  <span className="flex-1 truncate">{c.name}</span>
                  {count > 0 && (
                    <span className="text-2xs text-fg-subtle font-mono">{count}</span>
                  )}
                </NavLink>
              );
            })}
          </div>
        </div>

        {/* Icon-only categories — only on lg+ when collapsed */}
        {sidebarCollapsed && (
          <div className="space-y-1 hidden lg:block">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              return (
                <NavLink
                  key={c.id}
                  to={`/c/${c.id}`}
                  title={c.name}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center justify-center h-9 w-9 mx-auto rounded-md transition',
                      isActive
                        ? 'bg-surface text-fg'
                        : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
                    )
                  }
                >
                  <span
                    className={cn(
                      'size-5 rounded-md flex items-center justify-center bg-gradient-to-br border border-fg/85 dark:border-fg/40',
                      c.gradient,
                    )}
                  >
                    <Icon size={11} className="text-white drop-shadow-[0_1px_0_rgba(0,0,0,0.25)]" />
                  </span>
                </NavLink>
              );
            })}
          </div>
        )}
      </nav>

      {/* Footer — hide on lg+ when collapsed, always show on mobile */}
      <div
        className={cn(
          'px-4 py-3 border-t border-border text-2xs text-fg-subtle leading-relaxed',
          sidebarCollapsed && 'lg:hidden',
        )}
      >
        <div className="flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-success animate-pulse" />
          Runs locally in your browser
        </div>
      </div>
      </aside>
    </>
  );
}

function NavBtn({
  to,
  icon: Icon,
  label,
  collapsed,
  end,
}: {
  to: string;
  icon: typeof Home;
  label: string;
  collapsed: boolean;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 h-8 px-3 rounded-md text-sm transition-all',
          isActive
            ? 'bg-surface text-fg shadow-sm'
            : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
          /* Icon-only layout only applies on lg+ when collapsed */
          collapsed && 'lg:justify-center lg:px-0 lg:mx-auto lg:w-9',
        )
      }
    >
      <Icon size={14} className="shrink-0" />
      <span className={cn('truncate', collapsed && 'lg:hidden')}>{label}</span>
    </NavLink>
  );
}
